import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import './Labor.css';

function Labor({ onLogout }) {
  const navigate = useNavigate();
  const [laborServices, setLaborServices] = useState([]);
  const [jobgroups, setJobgroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJobgroup, setSelectedJobgroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 1 });

  const fetchJobgroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/labor/jobgroups', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setJobgroups(response.data.jobgroups || []);
    } catch (err) {
      console.error('Error fetching job groups:', err);
    }
  }, []);

  const fetchLaborServices = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/labor', {
        params: {
          page,
          pageSize,
          search: searchTerm.trim(),
          jobgroup_id: selectedJobgroup || undefined,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setLaborServices(response.data.laborServices || []);
      setPagination(response.data.pagination || { page, pageSize, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load labor services.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, selectedJobgroup]);

  useEffect(() => {
    fetchJobgroups();
  }, [fetchJobgroups]);

  useEffect(() => {
    fetchLaborServices();
  }, [fetchLaborServices]);

  const formatCell = (value) => {
    if (value === undefined || value === null || value === '') {
      return '-';
    }

    return value;
  };

  return (
    <div className="home-container app-shell-fixed">
      <Sidebar onLogout={onLogout} />
      <div className="main-section app-fixed-main">
        <div className="labor-container app-scroll-layout">
          <header className="labor-header">
            <button className="app-back-button" onClick={() => navigate('/inventory')}>
              Back
            </button>
            <h1>Labor Services</h1>
            <button className="btn-add" onClick={() => navigate('/inventory/labor/new')}>
              + Add Service
            </button>
          </header>

          <div className="labor-content">
            <div className="search-box">
              <div className="labor-toolbar">
                <input
                  type="text"
                  placeholder="Search labor items..."
                  value={searchTerm}
                  onChange={(e) => {
                    setPage(1);
                    setSearchTerm(e.target.value);
                  }}
                />
                <div className="labor-toolbar-actions">
                  <select
                    className="labor-filter"
                    value={selectedJobgroup}
                    onChange={(e) => {
                      setPage(1);
                      setSelectedJobgroup(e.target.value);
                    }}
                  >
                    <option value="">All job groups</option>
                    {jobgroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="labor-page-size"
                    value={pageSize}
                    onChange={(e) => {
                      setPage(1);
                      setPageSize(parseInt(e.target.value, 10));
                    }}
                  >
                    <option value={10}>10 rows</option>
                    <option value={20}>20 rows</option>
                    <option value={50}>50 rows</option>
                  </select>
                  <button className="labor-refresh" onClick={fetchLaborServices}>
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <div className="app-scroll-results">
              {loading ? (
                <div className="labor-loading">Loading labor services...</div>
              ) : error ? (
                <div className="labor-error">{error}</div>
              ) : laborServices.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">{'\u2699'}</div>
                  <h2>No Labor Services Found</h2>
                  <p>Add common labor services that you offer</p>
                </div>
              ) : (
                <div className="labor-table app-grid-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Job Group</th>
                        <th>Job Item</th>
                        <th>800-1600 Price</th>
                        <th>1600-2400 Price</th>
                        <th>2500-3200 Price</th>
                        <th>3500-5700 Price</th>
                        <th>800-1600 Min</th>
                        <th>1600-2400 Min</th>
                        <th>2400-3400 Min</th>
                        <th>3500-5700 Min</th>
                      </tr>
                    </thead>
                    <tbody>
                      {laborServices.map((service) => (
                        <tr key={service.id}>
                          <td>{service.id}</td>
                          <td>{service.jobgroup_name}</td>
                          <td>{service.description}</td>
                          <td>{formatCell(service.price_car_4c_800_1600)}</td>
                          <td>{formatCell(service.price_mid_1600_2400)}</td>
                          <td>{formatCell(service.price_large_2500_3200)}</td>
                          <td>{formatCell(service.price_xlarge_3500_5700)}</td>
                          <td>{formatCell(service.duration_800_1600)}</td>
                          <td>{formatCell(service.duration_1600_2400)}</td>
                          <td>{formatCell(service.duration_2400_3400)}</td>
                          <td>{formatCell(service.duration_3500_5700)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && (
                <div className="labor-pagination">
                  <div className="labor-page-info">
                    Showing page {pagination.page} of {pagination.totalPages} | Total rows: {pagination.total}
                  </div>
                  <div className="labor-page-buttons">
                    <button
                      className="labor-refresh"
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                      disabled={pagination.page <= 1}
                    >
                      Previous
                    </button>
                    <button
                      className="labor-refresh"
                      onClick={() => setPage((prev) => Math.min(prev + 1, pagination.totalPages))}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Labor;
